import { CheckOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import React from 'react';

import type { ChipOption } from './ChipSelect';

/**
 * {@link ChipSelect} where more than one value can be held at once.
 *
 * Same pill, same menu, three differences that a multiple choice needs:
 * the menu stays open while values are ticked, because picking two from a menu
 * that closes on the first is two trips; an empty choice is the absence of one
 * rather than a value; and the chip summarises a choice too wide to spell out.
 */

type ChipMultiSelectProps<V extends string> = {
  value: readonly V[];
  options: ReadonlyArray<ChipOption<V>>;
  onChange: (value: V[]) => void;
  /** Chip text while nothing is chosen. Name the action, not the empty state. */
  addLabel: string;
  /** The menu entry that unticks everything. */
  clearLabel: string;
  /** Chip text once more than one value is chosen, given `{ count }`. */
  summaryLabel: (count: number) => string;
  /** Accessible name. The chip already reads its own label, so it carries no tooltip. */
  title: string;
  disabled?: boolean;
  className?: string;
};

const CLEAR_KEY = '__clear__';

const Glyph = <V extends string>({ option }: { option: ChipOption<V> }) =>
  option.icon ?? <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: option.color }} />;

export function ChipMultiSelect<V extends string>({
  value,
  options,
  onChange,
  addLabel,
  clearLabel,
  summaryLabel,
  title,
  disabled,
  className,
}: ChipMultiSelectProps<V>) {
  const [open, setOpen] = React.useState(false);
  const chosen = options.filter((option) => value.includes(option.value));

  const items: MenuProps['items'] = [
    ...options.map((option) => ({
      key: option.value,
      label: (
        <span className="flex items-center gap-2 pr-1">
          <Glyph option={option} />
          <span className="flex-1">{option.label}</span>
          {value.includes(option.value) && <CheckOutlined className="text-[11px]" style={{ color: option.color }} />}
        </span>
      ),
    })),
    ...(chosen.length > 0
      ? [
          { type: 'divider' as const, key: 'clear-divider' },
          { key: CLEAR_KEY, label: <span className="text-slate-500">{clearLabel}</span> },
        ]
      : []),
  ];

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === CLEAR_KEY) {
      setOpen(false);
      onChange([]);
      return;
    }

    const picked = key as V;
    onChange(value.includes(picked) ? value.filter((held) => held !== picked) : [...value, picked]);
  };

  // One value keeps its own colour; several share the first's, because a chip cannot
  // wear four and a grey one would read as nothing being chosen.
  const face = chosen[0] ?? null;
  const label = chosen.length === 0 ? addLabel : chosen.length === 1 ? chosen[0].label : summaryLabel(chosen.length);

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      disabled={disabled}
      // `selectable` with `multiple` is what keeps the menu open across a pick: antd
      // closes a dropdown on any menu click unless the menu is a multiple choice, and
      // ticking two values through a menu that shuts on the first is two trips.
      //
      // No `selectedKeys` with it, deliberately. antd would paint the chosen rows with
      // `controlItemBgActive`, which this app derives from a `colorPrimary` of
      // `var(--color-app-primary)` - a string antd's palette cannot parse, so every
      // derived tint comes out near-black instead of a tint of the cyan. The tick is
      // drawn by the item itself, in the option's own colour, and needs no highlight.
      menu={{ items, onClick: onMenuClick, selectable: true, multiple: true, selectedKeys: [] }}
      placement="bottomLeft"
    >
      <button
        // `type` matters: these live inside the visit and client forms, where a
        // bare button submits the form.
        type="button"
        aria-label={title}
        disabled={disabled}
        className={
          'inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors disabled:cursor-default ' +
          (face ? '' : 'border-dashed border-slate-300 text-slate-400 hover:border-slate-400 hover:text-slate-600 ') +
          (className ?? '')
        }
        style={
          face
            ? {
                color: face.color,
                background: face.bg,
                borderColor: face.border ?? `${face.color}33`,
                boxShadow: open ? `0 0 0 2px ${face.color}22` : undefined,
              }
            : undefined
        }
      >
        <span className="flex text-[11px] leading-none">{face ? <Glyph option={face} /> : <PlusOutlined />}</span>
        <span className="leading-none">{label}</span>
        <DownOutlined className="text-[9px] leading-none opacity-70" />
      </button>
    </Dropdown>
  );
}

export default ChipMultiSelect;
